import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clipboard,
  Clock3,
  ExternalLink,
  Filter,
  Info,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Trash2,
  XCircle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { API_URL } from '../config/apiConfig';

const QUEUE_LIMIT = 100;
const QUEUE_REFRESH_MS = 30000;

const statusStyles = {
  queued: 'border-orange-500 text-orange-700 bg-orange-50',
  submitted: 'border-blue-500 text-blue-700 bg-blue-50',
  completed: 'border-green-600 text-green-700 bg-green-50',
  failed: 'border-amber-500 text-amber-700 bg-amber-50',
  error: 'border-red-600 text-red-700 bg-red-50'
};

const statusIcon = {
  queued: Clock3,
  submitted: Clock3,
  completed: CheckCircle2,
  failed: XCircle,
  error: XCircle
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  return format(new Date(dateString), 'MMM d, yyyy, h:mm a');
};

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (minutes < 60) return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
};

const copyText = async (text) => {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Clipboard can be blocked by browser permissions; ignore.
  }
};

const StatusBadge = ({ status }) => {
  const normalized = status || 'submitted';
  const Icon = statusIcon[normalized] || Info;
  return (
    <span className={`inline-flex min-w-[116px] items-center justify-center gap-1 rounded-md border px-3 py-1 text-xs font-semibold ${statusStyles[normalized] || 'border-gray-300 text-gray-700 bg-gray-50'}`}>
      <Icon className="h-3.5 w-3.5" />
      {normalized}
    </span>
  );
};

const FeedSearch = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('feeds');
  const [queueLogs, setQueueLogs] = useState([]);
  const [queueTotal, setQueueTotal] = useState(0);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueDeleting, setQueueDeleting] = useState(false);
  const [queueError, setQueueError] = useState('');
  const [queueLastRefresh, setQueueLastRefresh] = useState(null);
  const [selectedQueueLogIds, setSelectedQueueLogIds] = useState([]);
  const [filters, setFilters] = useState({
    feedId: '',
    storeId: '',
    status: ''
  });

  const [searchSku, setSearchSku] = useState('');
  const [skuLoading, setSkuLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [skuHistory, setSkuHistory] = useState(null);
  const [skuError, setSkuError] = useState('');
  const [skuTab, setSkuTab] = useState('details');

  const hasActiveFilters = useMemo(
    () => Boolean(filters.feedId || filters.storeId || filters.status),
    [filters]
  );

  const visibleQueueLogIds = useMemo(
    () => queueLogs.map(row => String(row.id)),
    [queueLogs]
  );

  const selectedVisibleCount = useMemo(
    () => visibleQueueLogIds.filter(id => selectedQueueLogIds.includes(id)).length,
    [selectedQueueLogIds, visibleQueueLogIds]
  );

  const fetchQueueLogs = async (nextFilters = filters, options = {}) => {
    if (!options.silent) {
      setQueueLoading(true);
    }
    setQueueError('');

    try {
      const params = new URLSearchParams();
      params.set('limit', String(QUEUE_LIMIT));
      if (nextFilters.feedId.trim()) params.set('feedId', nextFilters.feedId.trim());
      if (nextFilters.storeId.trim()) params.set('storeId', nextFilters.storeId.trim());
      if (nextFilters.status) params.set('status', nextFilters.status);

      const response = await fetch(`${API_URL}/feeds/queue-logs?${params.toString()}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load feed logs');
      }

      setQueueLogs(data.data || []);
      setQueueTotal(data.total || 0);
      setSelectedQueueLogIds(prev => prev.filter(id => (data.data || []).some(row => String(row.id) === id)));
      setQueueLastRefresh(new Date());
    } catch (err) {
      setQueueError(err.message || 'Failed to load feed logs');
    } finally {
      if (!options.silent) {
        setQueueLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchQueueLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeView !== 'feeds') return undefined;

    const intervalId = window.setInterval(() => {
      fetchQueueLogs(filters, { silent: true });
    }, QUEUE_REFRESH_MS);

    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, filters]);

  const handleQueueFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetQueueFilters = () => {
    const next = { feedId: '', storeId: '', status: '' };
    setFilters(next);
    fetchQueueLogs(next);
  };

  const handleQueueSubmit = (event) => {
    event.preventDefault();
    fetchQueueLogs();
  };

  const toggleQueueLogSelection = (id) => {
    const normalizedId = String(id);
    setSelectedQueueLogIds(prev => (
      prev.includes(normalizedId)
        ? prev.filter(selectedId => selectedId !== normalizedId)
        : [...prev, normalizedId]
    ));
  };

  const toggleAllVisibleQueueLogs = () => {
    setSelectedQueueLogIds(prev => {
      if (selectedVisibleCount === visibleQueueLogIds.length) {
        return prev.filter(id => !visibleQueueLogIds.includes(id));
      }

      const next = new Set(prev);
      visibleQueueLogIds.forEach(id => next.add(id));
      return Array.from(next);
    });
  };

  const deleteQueueLogs = async (ids) => {
    const normalizedIds = ids.map(id => Number.parseInt(id, 10)).filter(Number.isFinite);
    if (normalizedIds.length === 0) return;

    const noun = normalizedIds.length === 1 ? 'feed log' : 'feed logs';
    const confirmed = window.confirm(`Delete ${normalizedIds.length} ${noun}? This removes only the local UI log.`);
    if (!confirmed) return;

    setQueueDeleting(true);
    setQueueError('');

    try {
      const response = normalizedIds.length === 1
        ? await fetch(`${API_URL}/feeds/queue-logs/${normalizedIds[0]}`, { method: 'DELETE' })
        : await fetch(`${API_URL}/feeds/queue-logs`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids: normalizedIds })
          });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to delete feed logs');
      }

      setSelectedQueueLogIds(prev => prev.filter(id => !normalizedIds.includes(Number.parseInt(id, 10))));
      await fetchQueueLogs(filters, { silent: true });
    } catch (err) {
      setQueueError(err.message || 'Failed to delete feed logs');
    } finally {
      setQueueDeleting(false);
    }
  };

  const handleSkuSearch = async () => {
    if (!searchSku.trim()) {
      setSkuError('Please enter a SKU to search');
      return;
    }

    setSkuLoading(true);
    setSkuError('');
    setSearchResults(null);
    setSkuHistory(null);

    try {
      const searchResponse = await fetch(`${API_URL}/feeds/search/${searchSku.trim()}`);
      const searchData = await searchResponse.json();

      if (!searchResponse.ok) {
        throw new Error(searchData.message || 'Failed to search SKU');
      }

      setSearchResults(searchData);

      const historyResponse = await fetch(`${API_URL}/feeds/history/${searchSku.trim()}`);
      const historyData = await historyResponse.json();

      if (historyResponse.ok) {
        setSkuHistory(historyData);
      }
    } catch (err) {
      setSkuError(err.message || 'An error occurred while searching');
    } finally {
      setSkuLoading(false);
    }
  };

  const prepareChartData = () => {
    if (!skuHistory || !skuHistory.history) return [];

    return skuHistory.history
      .slice()
      .reverse()
      .map(item => ({
        date: format(new Date(item.created_at), 'MMM dd HH:mm'),
        quantity: parseInt(item.quantity, 10) || 0,
        fullDate: formatDateTime(item.created_at)
      }));
  };

  return (
    <div className="min-h-screen bg-white text-gray-950">
      <div className="border-b border-gray-200 bg-white">
        <div className="flex h-12 items-center justify-between px-5">
          <div className="flex items-center gap-3 text-sm">
            <button
              onClick={() => navigate('/')}
              className="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 hover:bg-gray-50"
              title="Back to dashboard"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <span className="font-semibold text-gray-500">Admin</span>
            <span className="text-gray-400">›</span>
            <span className="font-semibold">Feeds</span>
          </div>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <button
              onClick={() => setActiveView('feeds')}
              className={`rounded-md px-3 py-1.5 ${activeView === 'feeds' ? 'bg-gray-900 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              Feed Logs
            </button>
            <button
              onClick={() => setActiveView('sku')}
              className={`rounded-md px-3 py-1.5 ${activeView === 'sku' ? 'bg-gray-900 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              SKU Search
            </button>
          </div>
        </div>
      </div>

      {activeView === 'feeds' && (
        <>
          <form onSubmit={handleQueueSubmit} className="border-b border-gray-200 bg-white px-5 py-2">
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase text-gray-700">
              <button
                type="button"
                onClick={resetQueueFilters}
                className="inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-gray-100"
              >
                <RefreshCw className="h-4 w-4" />
                Reset
              </button>
              <button
                type="submit"
                className="inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-gray-100"
              >
                <Search className="h-4 w-4" />
                View
              </button>
              <div className="inline-flex items-center gap-1 rounded px-2 py-1">
                <Filter className="h-4 w-4" />
                Filters
              </div>
              <div className="ml-auto flex flex-wrap items-center gap-2 normal-case">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-gray-400" />
                  <input
                    value={filters.feedId}
                    onChange={(e) => handleQueueFilterChange('feedId', e.target.value)}
                    className="h-8 w-52 rounded border border-gray-300 pl-8 pr-2 text-sm font-normal outline-none focus:border-gray-900"
                    placeholder="Feed ID"
                  />
                </div>
                <input
                  value={filters.storeId}
                  onChange={(e) => handleQueueFilterChange('storeId', e.target.value)}
                  className="h-8 w-64 rounded border border-gray-300 px-2 text-sm font-normal outline-none focus:border-gray-900"
                  placeholder="Store ID"
                />
                <select
                  value={filters.status}
                  onChange={(e) => handleQueueFilterChange('status', e.target.value)}
                  className="h-8 rounded border border-gray-300 px-2 text-sm font-normal outline-none focus:border-gray-900"
                >
                  <option value="">All statuses</option>
                  <option value="queued">queued</option>
                  <option value="submitted">submitted</option>
                  <option value="completed">completed</option>
                  <option value="failed">failed</option>
                  <option value="error">error</option>
                </select>
                <button
                  type="submit"
                  className="inline-flex h-8 items-center gap-1 rounded border border-gray-900 px-3 text-sm font-semibold hover:bg-gray-900 hover:text-white"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  Apply
                </button>
              </div>
            </div>
          </form>

          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-2 text-xs font-semibold uppercase text-gray-600">
            <div className="flex items-center gap-5">
              <span>{queueTotal} records</span>
              {hasActiveFilters && <span className="text-gray-900">filtered</span>}
              {queueLoading && <span className="text-blue-700">loading</span>}
              {queueDeleting && <span className="text-red-700">deleting</span>}
              <span>auto refresh 30s</span>
              {queueLastRefresh && <span>updated {format(queueLastRefresh, 'h:mm:ss a')}</span>}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => deleteQueueLogs(selectedQueueLogIds)}
                disabled={selectedQueueLogIds.length === 0 || queueDeleting}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-red-700 hover:bg-red-50 disabled:text-gray-400 disabled:hover:bg-transparent"
              >
                <Trash2 className="h-4 w-4" />
                Delete selected {selectedQueueLogIds.length > 0 ? `(${selectedQueueLogIds.length})` : ''}
              </button>
              <button
                onClick={() => fetchQueueLogs()}
                className="inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-gray-100"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>

          {queueError && (
            <div className="mx-5 mt-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {queueError}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-[1460px] w-full border-collapse text-left text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-600">
                <tr>
                  <th className="w-10 px-5 py-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300"
                      checked={visibleQueueLogIds.length > 0 && selectedVisibleCount === visibleQueueLogIds.length}
                      onChange={toggleAllVisibleQueueLogs}
                      disabled={visibleQueueLogIds.length === 0}
                      title="Select all visible feed logs"
                    />
                  </th>
                  <th className="px-3 py-2">Actions</th>
                  <th className="px-3 py-2">Store</th>
                  <th className="px-3 py-2">Supplier</th>
                  <th className="px-3 py-2">Identifier</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Priority</th>
                  <th className="px-3 py-2">Processed</th>
                  <th className="px-3 py-2">Accepted</th>
                  <th className="px-3 py-2">Invalid</th>
                  <th className="px-3 py-2">Warnings</th>
                  <th className="px-3 py-2">Errors</th>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2">Started</th>
                  <th className="px-3 py-2">Completed</th>
                  <th className="px-3 py-2">Duration</th>
                </tr>
              </thead>
              <tbody>
                {queueLogs.map((row) => (
                  <tr key={row.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-5 py-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300"
                        checked={selectedQueueLogIds.includes(String(row.id))}
                        onChange={() => toggleQueueLogSelection(row.id)}
                        title="Select feed log"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => copyText(row.feed_id)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100"
                        title="Copy feed ID"
                      >
                        <Clipboard className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteQueueLogs([row.id])}
                        disabled={queueDeleting}
                        className="inline-flex h-8 w-8 items-center justify-center rounded text-red-700 hover:bg-red-50 disabled:text-gray-400 disabled:hover:bg-transparent"
                        title="Delete feed log"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                    <td className="px-3 py-2 font-medium">{row.store_name || row.store_id || 'Unknown'}</td>
                    <td className="px-3 py-2">{row.provider_id || '-'}</td>
                    <td className="px-3 py-2">
                      <div className="flex max-w-[190px] items-center gap-2">
                        <span className="truncate font-mono text-xs">{row.feed_id || '-'}</span>
                        {row.feed_id && (
                          <button onClick={() => copyText(row.feed_id)} title="Copy feed ID">
                            <Clipboard className="h-3.5 w-3.5 text-gray-500" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 rounded-md border border-violet-500 px-3 py-1 text-xs font-semibold text-violet-700">
                        <ExternalLink className="h-3.5 w-3.5" />
                        {row.phase || 'feed'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-3 py-2">{row.priority || 'normal'}</td>
                    <td className="px-3 py-2">{row.messages_processed ?? '-'}</td>
                    <td className="px-3 py-2">{row.messages_accepted ?? '-'}</td>
                    <td className="px-3 py-2">{row.messages_invalid ?? '-'}</td>
                    <td className="px-3 py-2">{row.warnings ?? '-'}</td>
                    <td className="px-3 py-2" title={row.error_message || ''}>
                      {row.errors ?? (row.error_message ? 1 : '-')}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        {formatDateTime(row.created_at)}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <Info className="h-4 w-4 text-gray-500" />
                        {formatDateTime(row.started_at)}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <Info className="h-4 w-4 text-gray-500" />
                        {formatDateTime(row.finished_at)}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDuration(row.duration_seconds)}</td>
                  </tr>
                ))}
                {!queueLoading && queueLogs.length === 0 && (
                  <tr>
                    <td colSpan="17" className="px-5 py-10 text-center text-sm text-gray-500">
                      No feed logs found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeView === 'sku' && (
        <div className="mx-auto max-w-7xl px-5 py-6">
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <div className="mb-5 flex items-center gap-2">
              <Search className="h-5 w-5" />
              <h1 className="text-xl font-bold">SKU Feed Search</h1>
            </div>

            <div className="mb-6 flex gap-2">
              <input
                type="text"
                placeholder="Enter SKU"
                value={searchSku}
                onChange={(e) => setSearchSku(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSkuSearch();
                }}
                className="h-10 flex-1 rounded border border-gray-300 px-3 outline-none focus:border-gray-900"
              />
              <button
                onClick={handleSkuSearch}
                disabled={skuLoading}
                className="rounded bg-gray-900 px-4 py-2 font-semibold text-white disabled:opacity-50"
              >
                {skuLoading ? 'Searching...' : 'Search'}
              </button>
            </div>

            {skuError && (
              <div className="mb-6 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {skuError}
              </div>
            )}

            {searchResults && searchResults.data && searchResults.data.length === 0 && (
              <div className="mb-6 rounded bg-gray-100 px-4 py-3 text-sm">
                No data found for SKU: {searchSku}
              </div>
            )}

            {searchResults && searchResults.data && searchResults.data.length > 0 && (
              <div>
                <div className="mb-4 flex gap-2">
                  {['details', 'history', 'raw'].map(tab => (
                    <button
                      key={tab}
                      className={`rounded px-4 py-2 text-sm font-semibold ${skuTab === tab ? 'bg-gray-900 text-white' : 'bg-gray-200'}`}
                      onClick={() => setSkuTab(tab)}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {skuTab === 'details' && (
                  <div className="space-y-4">
                    {searchResults.data.map((feed, index) => (
                      <div key={feed.feed_id} className="rounded border border-gray-200 p-5">
                        <div className="mb-4 flex items-start justify-between">
                          <div>
                            <h3 className="text-lg font-semibold">Feed #{index + 1}</h3>
                            <p className="text-gray-600">{formatDateTime(feed.created_at)}</p>
                          </div>
                          <div className="flex gap-2">
                            <span className="rounded-full bg-gray-900 px-3 py-1 text-sm text-white">
                              {feed.status}
                            </span>
                            <span className="rounded-full bg-gray-200 px-3 py-1 text-sm">
                              {feed.feed_type}
                            </span>
                          </div>
                        </div>

                        {feed.products.map((product, prodIndex) => (
                          <div key={prodIndex} className="space-y-3">
                            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                              <div>
                                <p className="text-sm text-gray-600">SKU</p>
                                <p className="font-mono">{product.sku}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Quantity</p>
                                <p className="font-semibold">{product.quantity || '0'}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Channel</p>
                                <p>{product.channel || 'DEFAULT'}</p>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600">Lead Time</p>
                                <p>{product.lead_time || 'N/A'} days</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                {skuTab === 'history' && (
                  <div className="space-y-4">
                    {skuHistory && skuHistory.history && skuHistory.history.length > 0 ? (
                      <>
                        <div className="rounded border border-gray-200 p-5">
                          <h3 className="mb-4 text-lg font-semibold">Quantity History</h3>
                          <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={prepareChartData()}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Line type="monotone" dataKey="quantity" stroke="#111827" strokeWidth={2} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                        <div className="rounded border border-gray-200 p-5">
                          <h3 className="mb-4 text-lg font-semibold">History Details</h3>
                          <div className="space-y-2">
                            {skuHistory.history.map((item, index) => (
                              <div key={index} className="flex items-center justify-between border-b py-2 last:border-0">
                                <div>
                                  <p className="text-sm">{formatDateTime(item.created_at)}</p>
                                  <p className="text-xs text-gray-600">{item.store_id} - {item.operation_type}</p>
                                </div>
                                <span className="rounded bg-gray-200 px-3 py-1 text-sm">
                                  Qty: {item.quantity}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="rounded bg-gray-100 px-4 py-3">No history data available for this SKU</div>
                    )}
                  </div>
                )}

                {skuTab === 'raw' && (
                  <div className="rounded border border-gray-200 p-5">
                    <h3 className="mb-4 text-lg font-semibold">Raw JSON Data</h3>
                    <pre className="max-h-96 overflow-auto rounded bg-gray-100 p-4 text-xs">
                      {JSON.stringify(searchResults, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedSearch;
